import * as R from 'ramda';

import { useTeamNavigation } from '@atom/hooks/useTeamNavigation';
import { Button, ListTable } from '@atom/mui';
import colors from '@atom/styles/colors';
import { TimeEntriesDailyTotal } from '@atom/types/timeEntry';
import { timeSheetTableWidths } from '@atom/utilities/timeSheetUtilities';
import { convertMillisToExactHours } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

const { TableRow, TableCell } = ListTable;

const secondaryHeaderCell = {
  top: '65px', // height of top header td with padding
  backgroundColor: colors.neutral.white,
};

const styles = {
  workInfo: {
    minWidth: timeSheetTableWidths.work,
    ...secondaryHeaderCell,
  },
  smallCell: {
    padding: '8px 4px',
    ...secondaryHeaderCell,
  },
  start: {
    padding: '8px',
    ...secondaryHeaderCell,
  },
  smallButton: {
    minWidth: '0',
    height: '24px',
    width: '24px',
    borderRadius: '12px',
  },
};

interface Props {
  dailyTotals: TimeEntriesDailyTotal[];
  selectedPayPeriodWeek: number;
  editTimeEntryId: string;
  createTimeEntry: boolean;
  dateFilter: number;
  setDateFilter: (date: number) => void;
}

const TimeSheetTableTotalsRow = ({
  dailyTotals = [],
  selectedPayPeriodWeek,
  editTimeEntryId,
  createTimeEntry,
  dateFilter,
  setDateFilter,
}: Props) => {
  const { teamNavigation } = useTeamNavigation();
  const { payPeriodWeeks } = teamNavigation;

  const currentWeekDates = payPeriodWeeks[selectedPayPeriodWeek] || [];

  const isEditMode = !isNilOrEmpty(editTimeEntryId) || createTimeEntry;

  const toggleDateFilter = (date: number) => {
    setDateFilter(dateFilter === date ? null : date);
  };

  return (
    <>
      <TableRow header key="totals" hover={false}>
        <TableCell
          width={timeSheetTableWidths.status}
          style={styles.start}
          center
        />
        <TableCell
          width={timeSheetTableWidths.workType}
          style={secondaryHeaderCell}
        />
        <TableCell style={styles.workInfo} />
        <TableCell
          width={timeSheetTableWidths.task}
          style={secondaryHeaderCell}
        />
        <TableCell
          width={timeSheetTableWidths.userGroup}
          style={secondaryHeaderCell}
        />
        <TableCell
          width={timeSheetTableWidths.budget}
          style={secondaryHeaderCell}
        />
        <TableCell
          width={timeSheetTableWidths.small}
          center
          style={styles.smallCell}
        />
        {currentWeekDates?.map(day => {
          const duration = R.pathOr(
            undefined,
            ['duration'],
            R.find(R.propEq(day, 'date'))(dailyTotals),
          );

          const text = duration ? convertMillisToExactHours(duration) : '';
          const textColor =
            dateFilter === day
              ? colors.neutral.white
              : convertMillisToExactHours(duration) > 24
                ? colors.brand.red
                : colors.neutral.dim;

          const buttonStyles = {
            ...styles.smallButton,
            color: textColor,
            backgroundColor:
              dateFilter === day ? colors.brand.blue : colors.neutral.white,
          };

          return (
            <TableCell
              width={timeSheetTableWidths.small}
              center
              style={styles.smallCell}
              key={day}
            >
              {!!duration && (
                <Button
                  style={buttonStyles}
                  onClick={() => toggleDateFilter(day)}
                >
                  {text}
                </Button>
              )}
            </TableCell>
          );
        })}
        <TableCell
          width={timeSheetTableWidths.small}
          center
          style={styles.smallCell}
        />
        {isEditMode && (
          <TableCell
            width={timeSheetTableWidths.action}
            style={styles.smallCell}
            center
          />
        )}
        <TableCell
          width={timeSheetTableWidths.action}
          style={styles.smallCell}
          center
        />
      </TableRow>
    </>
  );
};

export default TimeSheetTableTotalsRow;
